import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Calendar, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import CountdownTimer from "./CountdownTimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HeroSection = () => {
  const [heroVideo, setHeroVideo] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Early Bird Configuration
  const EARLY_BIRD_DEADLINE = "2026-09-05T23:59:59";
  const isEarlyBirdActive = new Date() < new Date(EARLY_BIRD_DEADLINE);

  // Upcoming events data
  const upcomingEvents = [
    {
      id: "hyderabad-2027",
      name: "HYDERABAD 2027",
      city: "Hyderabad",
      location: "Gachibowli Stadium, Hyderabad",
      date: "February 7, 2027",
      targetDate: "2027-02-07T05:30:00",
      color: "#FF1493", // Hot Pink
      edition: "3rd Edition",
      pricing: { "3k": 799, "5k": 999, "10k": 1199 },
      earlyBird: null,
      announcingSoon: false
    }
  ];

  // Check if early bird is active (legacy function - kept for compatibility)
  const isEarlyBird = (event) => {
    return isEarlyBirdActive;
  };

  useEffect(() => {
    const fetchHeroVideo = async () => {
      try {
        const response = await axios.get(`${API}/settings/hero-video`);
        if (response.data.url) {
          setHeroVideo(response.data);
        }
      } catch (e) {
        console.error("Error fetching hero video:", e);
      }
    };
    fetchHeroVideo();
  }, []);

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % upcomingEvents.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, upcomingEvents.length]);

  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + upcomingEvents.length) % upcomingEvents.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, upcomingEvents.length]);

  const goToSlide = useCallback((index) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, currentSlide]);

  // Auto-slide every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 4000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const currentEvent = upcomingEvents[currentSlide];

  const getVideoEmbed = () => {
    if (!heroVideo?.url) return null;

    if (heroVideo.type === "youtube") {
      const match = heroVideo.url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
      if (match) {
        return `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=1&loop=1&playlist=${match[1]}&controls=0&showinfo=0`;
      }
    }
    
    if (heroVideo.type === "vimeo") {
      const match = heroVideo.url.match(/vimeo\.com\/(\d+)/);
      if (match) {
        return `https://player.vimeo.com/video/${match[1]}?autoplay=1&muted=1&loop=1&background=1`;
      }
    }

    return heroVideo.url;
  };

  return (
    <section 
      className="relative min-h-screen flex items-center justify-center hero-video-container overflow-hidden"
      data-testid="hero-section"
    >
      {/* Video Background - Use real past edition image as poster */}
      {heroVideo?.url ? (
        heroVideo.type === "url" ? (
          <video 
            src={heroVideo.url}
            autoPlay
            muted
            loop
            playsInline
            poster="https://customer-assets.emergentagent.com/job_chennai-finrun/artifacts/32jrksgc_DR6A3677.JPG"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <iframe
            src={getVideoEmbed()}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ transform: "scale(1.5)" }}
            frameBorder="0"
            allow="autoplay; fullscreen"
            title="Hero Video"
          />
        )
      ) : (
        /* Fallback: Real image from Hyderabad 2024 edition */
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_chennai-finrun/artifacts/32jrksgc_DR6A3677.JPG"
            alt="The FinRun Hyderabad 2024"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Overlay */}
      <div className="hero-overlay" />

      {/* Slide Navigation Arrows */}
      <button 
        onClick={prevSlide}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-all group"
        data-testid="prev-slide-btn"
      >
        <ChevronLeft className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
      </button>
      <button 
        onClick={nextSlide}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-all group"
        data-testid="next-slide-btn"
      >
        <ChevronRight className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
      </button>

      {/* Sliding Content */}
      <div className="relative z-20 container-custom text-center text-white pt-16">
        {/* Content with slide animation */}
        <div 
          key={currentSlide}
          className={`transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-[#FF3B30]/20 backdrop-blur-sm border border-[#FF3B30]/30 rounded-full px-4 py-2 mb-6">
            <span className="w-2 h-2 bg-[#FF3B30] rounded-full animate-pulse" />
            <span className="text-sm font-medium tracking-wide">INDIA'S FIRST & LARGEST CYBER AWARENESS RUN</span>
          </div>

          {/* Main Heading - Moved down with more margin */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black mb-6 mt-4 font-['Outfit'] leading-tight">
            THE <span className="text-white font-black">FIN</span>RUN
            <br />
            <span style={{ color: currentEvent.color }}>{currentEvent.name}</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-4">
            Run for Cyber Safety. Stand for Digital Trust.
          </p>

          {/* Super Early Bird Banner with Countdown */}
          {isEarlyBirdActive && (
            <div className="bg-gradient-to-r from-[#00C853]/30 to-[#00E676]/30 backdrop-blur-sm border border-[#00C853]/50 rounded-2xl px-6 py-4 mb-6 max-w-lg mx-auto">
              <p className="text-[#00E676] font-bold text-sm sm:text-base mb-2">🎉 SUPER EARLY BIRD — 20% OFF ALL TICKETS!</p>
              <p className="text-white/80 text-xs mb-2">Offer ends September 5th at 11:59 PM</p>
              <CountdownTimer 
                targetDate={EARLY_BIRD_DEADLINE}
                compact={true}
              />
            </div>
          )}

          {/* Event Info Pills */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2.5">
              <Calendar className="w-5 h-5 text-[#FFD700]" />
              <span className="font-semibold">{currentEvent.date}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2.5">
              <MapPin className="w-5 h-5 text-[#FF3B30]" />
              <span className="font-semibold">{currentEvent.location}</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {currentEvent.announcingSoon ? (
              <>
                <Link to="/sponsor">
                  <Button 
                    className="btn-primary text-lg px-10 py-6 animate-pulse-glow"
                    data-testid="hero-register-btn"
                  >
                    BECOME A SPONSOR
                  </Button>
                </Link>
                <Link to="/#contact">
                  <Button 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] rounded-full text-lg px-10 py-6 transition-all"
                    data-testid="hero-notify-btn"
                  >
                    NOTIFY ME
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to={`/register/${currentEvent.id}`}>
                  <Button 
                    className="btn-primary text-lg px-10 py-6 animate-pulse-glow"
                    data-testid="hero-register-btn"
                  >
                    REGISTER NOW
                  </Button>
                </Link>
                <Link to="/sponsor">
                  <Button 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] rounded-full text-lg px-10 py-6 transition-all"
                    data-testid="hero-sponsor-btn"
                  >
                    BECOME A SPONSOR
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Stats / Pricing */}
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto mt-12">
            <div>
              <p className="text-4xl sm:text-5xl font-black font-['Outfit'] text-[#FFD700]">3K</p>
              {isEarlyBirdActive ? (
                <div>
                  <p className="text-sm text-[#00C853] font-bold">₹{Math.round(currentEvent.pricing["3k"] * 0.8)}</p>
                  <p className="text-xs text-gray-500 line-through">₹{currentEvent.pricing["3k"]}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-400">Fun Run ₹{currentEvent.pricing["3k"]}</p>
              )}
            </div>
            <div>
              <p className="text-4xl sm:text-5xl font-black font-['Outfit'] text-[#FF3B30]">5K</p>
              {isEarlyBirdActive ? (
                <div>
                  <p className="text-sm text-[#00C853] font-bold">₹{Math.round(currentEvent.pricing["5k"] * 0.8)}</p>
                  <p className="text-xs text-gray-500 line-through">₹{currentEvent.pricing["5k"]}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-400">Timed ₹{currentEvent.pricing["5k"]}</p>
              )}
            </div>
            <div>
              <p className="text-4xl sm:text-5xl font-black font-['Outfit'] text-[#007AFF]">10K</p>
              {isEarlyBirdActive ? (
                <div>
                  <p className="text-sm text-[#00C853] font-bold">₹{Math.round(currentEvent.pricing["10k"] * 0.8)}</p>
                  <p className="text-xs text-gray-500 line-through">₹{currentEvent.pricing["10k"]}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-400">Timed ₹{currentEvent.pricing["10k"]}</p>
              )}
            </div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="flex items-center justify-center gap-3 mt-10">
          {upcomingEvents.map((event, index) => (
            <button
              key={event.id}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide 
                  ? 'w-10 h-3 bg-white' 
                  : 'w-3 h-3 bg-white/40 hover:bg-white/60'
              }`}
              data-testid={`slide-indicator-${index}`}
              aria-label={`Go to ${event.city} event`}
            />
          ))}
        </div>

        {/* Event Labels */}
        <div className="flex items-center justify-center gap-8 mt-4">
          {upcomingEvents.map((event, index) => (
            <button
              key={event.id}
              onClick={() => goToSlide(index)}
              className={`text-sm font-medium transition-all ${
                index === currentSlide 
                  ? 'text-white' 
                  : 'text-white/50 hover:text-white/80'
              }`}
            >
              {event.city.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
